// src/components/reports/PurchasingReportPanel.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Drawer, IconButton, TextField, Button, Typography,
  Stack, Divider, List, ListItemButton, ListItemText,
  CircularProgress, Card, CardContent
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import UpdateIcon from "@mui/icons-material/Update";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import toast from "react-hot-toast";

import {
  listPurchasingReportDefinitions,
  createPurchasingReportDefinition,
  getPurchasingReportDefinition,
  updatePurchasingReportDefinition,
  pinPurchasingReportDefinition,
  deletePurchasingReportDefinition,
  publishPurchasingReportInvalidation,
} from "../../../../../services/purchasingReport";

// ---- shape helpers ---------------------------------------------------------

/** What we store inside config_json (forward/backward-compatible) */
const makeConfig = ({ instanceUniqueId, filters, visibleCols, pinned = false }) => ({
  version: 2,
  reportKey: "purchasingDashboard",
  instanceUniqueId,
  pinned,
  ui: {
    filters,
    visibleCols,
  },
});


/** Pull {filters, visibleCols} back out of a definition */
const readConfig = (def) => {
  const cfg = def?.config_json || def?.config || {};
  const ui = cfg?.ui || def?.ui || {};
  return {
    filters: ui.filters ?? null,
    visibleCols: ui.visibleCols ?? null,
  };
};

// Helper: is this definition pinned?
const isPinned = (def) =>
  Boolean(
    def?.config_json?.pinned ??
    def?.config?.pinned ??
    def?.config_json?.ui?.pinned ??
    def?.config?.ui?.pinned ??
    false
  );

const toTagArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try { const x = JSON.parse(val); return Array.isArray(x) ? x : []; } catch {}
    return val.split(",").map(s => s.trim()).filter(Boolean);
  }
  return [];
};

// Stable blank filters shape (fallback/default)
const INITIAL_FILTERS = {
  clientRepId: null,
  orderRepId: null,
  productionRepId: null,
  clientIds: [],
  supplierIds: [],
  shippingMethods: [],
  status: [],          // legacy alias (UI label: “Status”)
  stage: [],  
  proofStatus: [],
  productionRepStatus: [],
  supplierStage: [],
  firm: null,
  nextActionTypes: [],
  dateType: "nextActionDate",
  dateFrom: "",
  dateTo: "",
  dateQuick: "any",
  dateCustom: ""
};

// Normalize before saving so arrays are arrays, ids are strings, etc.
const normalizeForSave = (f = INITIAL_FILTERS) => {
    // fold legacy aliases into canonical keys
    const supplierStage =
      Array.isArray(f.supplierStage) ? f.supplierStage
      : Array.isArray(f.stage) ? f.stage
      : f.stage ? [f.stage] : [];
  
    const productionRepStatus =
      Array.isArray(f.productionRepStatus) ? f.productionRepStatus
      : Array.isArray(f.status) ? f.status
      : f.status ? [f.status] : [];
  
    const withAliases = { ...f, supplierStage, productionRepStatus };
  
    return {
      ...INITIAL_FILTERS,
      ...withAliases,
      clientIds: (withAliases.clientIds || []).map(String),
      supplierIds: (withAliases.supplierIds || []).map(String),
      shippingMethods: Array.isArray(withAliases.shippingMethods) ? withAliases.shippingMethods : [],
      proofStatus: Array.isArray(withAliases.proofStatus) ? withAliases.proofStatus : [],
      productionRepStatus,
      supplierStage,
      nextActionTypes: Array.isArray(withAliases.nextActionTypes) ? withAliases.nextActionTypes : [],
      productionRepId: withAliases.productionRepId ?? null,
    };
  };

// ---- component -------------------------------------------------------------

export function ReportingPanel({
  open,
  onClose,
  instanceUniqueId,
  filters,          // current effective UI filters (parent passes effectiveFilters)
  visibleCols,      // current visible column keys (from dashboard)
  onApplyDefinition, // fn({filters, visibleCols}) => apply to UI & fetch
  onPinsChanged
}) {
  const [defs, setDefs] = useState([]);
  const [defsLoading, setDefsLoading] = useState(false);
  const [query, setQuery] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  const [saving, setSaving] = useState(false);

  // CREATE form
  const [createTitle, setCreateTitle] = useState("");
  const [createDesc, setCreateDesc] = useState("");

  // EDIT form
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [visibility, setVisibility] = useState("private"); // harmless to keep in payload
  const [tagsStr, setTagsStr] = useState("");

  // Drafts mirror the dashboard state we intend to save
  const [draftFilters, setDraftFilters] = useState(INITIAL_FILTERS);
  const [draftVisibleCols, setDraftVisibleCols] = useState([]);

  // Sync drafts whenever the panel opens or the parent filters/columns change
  useEffect(() => {
    if (!open) return;
    setDraftFilters(filters ?? INITIAL_FILTERS);
    setDraftVisibleCols(visibleCols ?? []);
  }, [open, filters, visibleCols]);

  // Load list
  const refreshDefs = useCallback(async () => {
    if (!open) return;
    try {
      setDefsLoading(true);
      const base = query ? { q: query } : {};
      const data = await listPurchasingReportDefinitions(instanceUniqueId, { ...base, limit: 200 });
      setDefs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load saved reports");
    } finally {
      setDefsLoading(false);
    }
  }, [instanceUniqueId, query, open]);

  // Load a selected definition
  const loadSelected = useCallback(async (id) => {
    if (!id) { setSelected(null); return; }
    try {
      const def = await getPurchasingReportDefinition(id, instanceUniqueId);
      setSelected(def || null);
      // fill EDIT form
      setEditTitle(def?.name || "");
      setEditDesc(def?.description || "");
      setVisibility(def?.visibility || "private");
      setTagsStr(toTagArray(def?.tags).join(", "));
    } catch (e) {
      console.error(e);
      toast.error("Could not open report");
    }
  }, [instanceUniqueId]);

  // initial + when open/search changes
  useEffect(() => { refreshDefs(); }, [refreshDefs]);

  // When panel closes, clear selection & forms
  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setSelected(null);
      setCreateTitle("");
      setCreateDesc("");
      setEditTitle("");
      setEditDesc("");
    }
  }, [open]);

  // Apply selection fetch
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);

  const applyToUi = () => {
    if (!selected) return;
    const { filters: f, visibleCols: cols } = readConfig(selected);
    if (!f && !cols) { toast("This saved report has no UI config yet."); return; }
    onApplyDefinition?.({ filters: f || null, visibleCols: cols || null });
    toast.success("Report filters applied");
  };

  // Toggle pinned status on a definition (minimal update)
  const togglePin = useCallback(async (def) => {
    if (!def?.id) return;
    const requestedPinned = !isPinned(def);
    try {
      const updated = await pinPurchasingReportDefinition(def.id, {
        instanceUniqueId,
        pinned: requestedPinned,
      });
      if (isPinned(updated) !== requestedPinned) {
        throw new Error(`Pin persistence mismatch: requested ${requestedPinned}, received ${isPinned(updated)}`);
      }
      if (selected?.id === def.id) setSelected(updated || def);
      toast.success(requestedPinned ? "Pinned" : "Unpinned");
      publishPurchasingReportInvalidation(instanceUniqueId);
      onPinsChanged?.();               
      await refreshDefs();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Could not update pin");
    }
  }, [instanceUniqueId, onPinsChanged, refreshDefs, selected]);

  // Create → Save
  const saveNew = async () => {
    const title = (createTitle || "").trim();
    if (!title) { toast("Please enter a title."); return; }
    try {
      setSaving(true);
      const filtersToSave = normalizeForSave(filters ?? draftFilters);
      const colsToSave    = visibleCols ?? draftVisibleCols;
      const payload = {
        instanceUniqueId,
        name: title,
        description: (createDesc || "").trim() || undefined,
        visibility,                      // harmless even if hidden
        tags: toTagArray(tagsStr),       // harmless even if hidden
        config_json: makeConfig({ instanceUniqueId, filters: filtersToSave, visibleCols: colsToSave, pinned: false }),

      };
      const created = await createPurchasingReportDefinition(payload, instanceUniqueId);
      toast.success("Report saved");
      publishPurchasingReportInvalidation(instanceUniqueId);
      // jump into EDIT mode for the new one
      setSelectedId(created?.id ?? null);
      onPinsChanged?.();               
      setCreateTitle("");
      setCreateDesc("");
      await refreshDefs();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Could not save report");
    } finally {
      setSaving(false);
    }
  };

  // Edit → Update
  const updateExisting = async () => {
    if (!selected?.id) return;
    const title = (editTitle || "").trim();
    if (!title) { toast("Please enter a title."); return; }
    try {
      setSaving(true);
      const currentlyPinned = isPinned(selected);
      const filtersToSave = normalizeForSave(filters ?? draftFilters);
      const colsToSave    = visibleCols ?? draftVisibleCols;
      const payload = {
        name: title,
        description: (editDesc || "").trim() || undefined,
        visibility,
        tags: toTagArray(tagsStr),
        config_json: makeConfig({
          instanceUniqueId,
                 filters: filtersToSave,
                 visibleCols: colsToSave,
          pinned: currentlyPinned,
        }),
      };
      const updated = await updatePurchasingReportDefinition(selected.id, payload, instanceUniqueId);
      toast.success("Report updated");
      publishPurchasingReportInvalidation(instanceUniqueId);
      setSelected(updated);
      onPinsChanged?.();               
      await refreshDefs();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Could not update report");
    } finally {
      setSaving(false);
    }
  };

  const deleteDef = async () => {
    if (!selected?.id) return;
    const ok = window.confirm(`Delete "${selected.name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await deletePurchasingReportDefinition(selected.id, instanceUniqueId);
      toast.success("Deleted");
      publishPurchasingReportInvalidation(instanceUniqueId);
      setSelectedId(null);
      setSelected(null);
      setEditTitle("");
      setEditDesc("");
      onPinsChanged?.();               
      await refreshDefs();
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  };

  // defs list (left)
  const sortedDefs = useMemo(() => {
    const arr = Array.isArray(defs) ? [...defs] : [];
    arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    return arr;
  }, [defs]);

  const resetToCreate = () => {
    setSelectedId(null);
    setSelected(null);
    setEditTitle("");
    setEditDesc("");
  };

  const deleteDefInline = useCallback(async (def) => {
    if (!def?.id) return;
    const ok = window.confirm(`Delete "${def.name || "Untitled"}"? This cannot be undone.`);
    if (!ok) return;
  
    try {
      await deletePurchasingReportDefinition(def.id, instanceUniqueId);
      toast.success("Deleted");
      publishPurchasingReportInvalidation(instanceUniqueId);
  
      // if the deleted one was selected, clear the edit form
      if (String(selectedId) === String(def.id)) {
        setSelectedId(null);
        setSelected(null);
        setEditTitle("");
        setEditDesc("");
      }
  
      onPinsChanged?.();
      await refreshDefs();
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  }, [instanceUniqueId, selectedId, refreshDefs, onPinsChanged]);
  

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 960 } }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "340px 1fr", height: "100%" }}>
        {/* LEFT – Saved reports list */}
        <Box sx={{ borderRight: "1px solid #eee", p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6">Reports</Typography>
            <IconButton onClick={onClose} size="large"><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search saved reports…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ endAdornment: <SearchIcon fontSize="small" /> }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={resetToCreate}
              sx={{ textTransform: "unset", whiteSpace: "nowrap" }}
              title="Start a new report definition"
            >
              New
            </Button>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {defsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}><CircularProgress size={24} /></Box>
          ) : sortedDefs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No saved reports yet.</Typography>
          ) : (
            <List dense sx={{ overflow: "auto", maxHeight: "calc(100vh - 210px)" }}>
              {sortedDefs.map(d => {
                const pinned = isPinned(d);
                return (
                  <ListItemButton
                    key={d.id}
                    selected={String(selectedId) === String(d.id)}
                    onClick={() => setSelectedId(d.id)}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <ListItemText
                      primary={<span style={{ fontWeight: 700 }}>{d.name || "Untitled"}</span>}
                      secondary={d.description || null}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        title={pinned ? "Unpin" : "Pin"}
                        onClick={(e) => { e.stopPropagation(); togglePin(d); }}
                      >
                        {pinned ? <StarIcon color="warning" /> : <StarBorderIcon />}
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); deleteDefInline(d); }}
                      >
                        <DeleteForeverIcon color="error" />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

        {/* RIGHT – Create vs Edit */}
        <Box sx={{ p: 2, display: "grid", gridTemplateRows: "auto 1fr auto", gap: 2 }}>
          {/* header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
            <Typography variant="h6">Saved reports</Typography>
            <IconButton onClick={onClose} size="large"><CloseIcon /></IconButton>
          </Box>

          {/* forms */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            {/* CREATE NEW */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Create New
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    label="Title (required)"
                    value={createTitle}
                    onChange={(e) => setCreateTitle(e.target.value)}
                    required
                    placeholder="e.g., Late orders this week"
                  />

                  <TextField
                    label="Description (optional)"
                    value={createDesc}
                    onChange={(e) => setCreateDesc(e.target.value)}
                    placeholder="Optional notes"
                    multiline
                    minRows={2}
                    sx={{ gridColumn: "1 / span 2" }}
                  />

                  <Box sx={{ gridColumn: "1 / span 2", display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveNew}
                      disabled={saving || !(createTitle || "").trim()}
                      sx={{ textTransform: "unset" }}
                    >
                      {saving ? "Saving…" : "Save current filters as new"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* EDIT SELECTED (only if a report is selected) */}
            {selected && (
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Edit Selected
                    </Typography>
                    <IconButton
                      size="small"
                      title={isPinned(selected) ? "Unpin" : "Pin"}
                      onClick={() => togglePin(selected)}
                    >
                      {isPinned(selected) ? <StarIcon color="warning" /> : <StarBorderIcon />}
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      label="Title (required)"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      required
                    />

                    <TextField
                      label="Description (optional)"
                      value={editDesc}
                      onChange={(e) => setEditDesc(e.target.value)}
                      multiline
                      minRows={2}
                      sx={{ gridColumn: "1 / span 2" }}
                    />

                    <Box sx={{ gridColumn: "1 / span 2", display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        startIcon={<UpdateIcon />}
                        onClick={updateExisting}
                        disabled={saving || !(editTitle || "").trim()}
                        sx={{ textTransform: "unset" }}
                      >
                        {saving ? "Updating…" : "Update saved report with current filters"}
                      </Button>

                      <Button variant="text" onClick={applyToUi} sx={{ textTransform: "unset" }}>
                        Apply to table
                      </Button>

                      <Button
                        color="error"
                        variant="text"
                        startIcon={<DeleteForeverIcon />}
                        onClick={deleteDef}
                        sx={{ ml: "auto", textTransform: "unset" }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* footer */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Saved reports capture your filters and visible columns.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={onClose}>Close</Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
